import React, { useCallback, useState, useEffect, useRef } from 'react';
// AuthContext import reserved for future per-user doc association
// import { useAuth } from '../../../contexts/AuthContext';
import FileUpload from '../../../components/FileUpload';
import { DocumentEndeavorApi } from '../../../services/api/DocumentEndeavorAPI';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';

/* ── Inline SVG icons (single-color, 18px) ── */
const PodcastIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 18v-6a9 9 0 0118 0v6" />
    <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
  </svg>
);

const AudiobookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 10v3" /><path d="M6 6v11" /><path d="M10 3v18" /><path d="M14 8v7" /><path d="M18 5v13" /><path d="M22 10v3" />
  </svg>
);

const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const FlashcardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="6" width="16" height="14" rx="2" />
    <path d="M6 2h14a2 2 0 012 2v12" />
  </svg>
);

const QuizIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const GuideIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
  </svg>
);

/**
 * Step 6 — Upload Course Documents
 * Let users upload lecture notes, textbook PDFs etc. and link to the just-created course.
 * Supports multi-course mode via courseIndex prop.
 */
export default function DocsUploadStep({ data, updateData, goNext, goBack, skipStep, courseIndex = 0, totalCourses = 1 }) {

  // Resolve courseId/courseName for this iteration
  const course = data.courses?.[courseIndex] || {};
  const courseId = course.courseId || data.courseId;
  const courseName = course.courseName || data.courseName || 'your course';
  const isLastCourse = courseIndex >= totalCourses - 1;

  // Per-file processing states: { docId: 'processing' | 'ready' }
  const [fileStates, setFileStates] = useState({});
  const [fileNames, setFileNames] = useState({}); // { docId: fileName }
  const readyTimers = useRef({});

  // Clean up timers on unmount
  useEffect(() => {
    const timers = readyTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const handleUploadComplete = useCallback(async (uploadedDoc) => {
    const docId = uploadedDoc?.documentId || uploadedDoc?.id;
    if (!docId) return;

    const fileName = uploadedDoc?.fileName || uploadedDoc?.name || uploadedDoc?.title || `Document`;

    // Set processing state
    setFileStates(prev => ({ ...prev, [docId]: 'processing' }));
    setFileNames(prev => ({ ...prev, [docId]: fileName }));

    // Use functional updater to avoid stale closures when multiple
    // files upload in the same render cycle (React 18 batching).
    updateData(prev => {
      const globalDocs = [...(prev.uploadedDocIds || []), docId];
      const updatedCourses = [...(prev.courses || [])];
      if (updatedCourses[courseIndex]) {
        updatedCourses[courseIndex] = {
          ...updatedCourses[courseIndex],
          uploadedDocIds: [...(updatedCourses[courseIndex].uploadedDocIds || []), docId],
        };
      }
      return { uploadedDocIds: globalDocs, courses: updatedCourses };
    });

    // Associate with course if we have one
    if (courseId) {
      try {
        await DocumentEndeavorApi.associateDocumentWithEndeavor(docId, courseId);
      } catch (e) {
        console.warn('[DocsUploadStep] Failed to associate document:', e);
      }
    }

    // Transition to ready after a delay
    readyTimers.current[docId] = setTimeout(() => {
      setFileStates(prev => ({ ...prev, [docId]: 'ready' }));
    }, 2200);

    UsageEventsTracker.recordUserAction('ONBOARDING_DOC_UPLOADED', {
      action: 'upload',
      category: 'onboarding',
      entityType: 'document',
      entityId: docId,
      metadata: { courseId, courseIndex },
    });
  }, [courseId, courseIndex, updateData]);

  const totalDocs = course.uploadedDocIds?.length || 0;
  const uploadedDocIds = course.uploadedDocIds || [];

  // Dynamic "Next" button label
  const nextLabel = isLastCourse ? 'Next: Try AI Tools →' : `Next: Review Course ${courseIndex + 2} →`;

  return (
    <div className="onboarding-card duc-card">
      <div className="onboarding-card-header">
        {totalCourses > 1 && (
          <p className="onboarding-course-counter">
            Course {courseIndex + 1} of {totalCourses}
          </p>
        )}
        <h2 className="duc-headline">Power your course with real materials</h2>
        <p className="duc-subtitle">
          Upload lecture slides, notes, or PDFs and Scout will turn them into quizzes, flashcards, and exam prep automatically.
        </p>
      </div>

      <FileUpload
        onUploadComplete={handleUploadComplete}
        onUploadError={(errorMessage, fileName) => {
          UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
            action: 'upload_failure',
            category: 'onboarding',
            label: 'docs-upload',
            metadata: { courseIndex, errorMessage, fileName },
          });
        }}
        endeavorId={courseId}
        scoutIQMode={true}
      />

      {/* Per-file processing states */}
      {uploadedDocIds.length > 0 && (
        <div className="duc-file-states">
          {uploadedDocIds.map(docId => {
            const state = fileStates[docId] || 'ready';
            const name = fileNames[docId] || 'Document';
            return (
              <div key={docId} className={`duc-file-state duc-file-state--${state}`}>
                <span className="duc-file-state-icon">
                  {state === 'processing' ? (
                    <span className="duc-file-spinner" />
                  ) : '✓'}
                </span>
                <span className="duc-file-state-name">{name}</span>
                <span className="duc-file-state-label">
                  {state === 'processing' ? 'Scout is analyzing your material…' : 'Ready for AI tools'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline example + hint */}
      <p className="duc-inline-example">
        Upload your syllabus → get podcasts, quizzes, and a full study system instantly
      </p>
      {totalDocs < 2 && (
        <p className="duc-hint">
          Best results: upload at least 2–3 files
        </p>
      )}

      {/* Transition label */}
      <p className="duc-grid-label">Turn your files into:</p>

      {/* Feature grid — 2-column compact tiles */}
      <div className={`duc-feature-grid${totalDocs > 0 ? ' duc-feature-grid--unlocked' : ''}`}>
        <div className="duc-feature-tile duc-feature-tile--highlight">
          <span className="duc-feature-tile-icon"><PodcastIcon /></span>
          <span className="duc-feature-tile-label">Podcasts</span>
        </div>
        <div className="duc-feature-tile duc-feature-tile--highlight">
          <span className="duc-feature-tile-icon"><AudiobookIcon /></span>
          <span className="duc-feature-tile-label">Audiobooks</span>
        </div>
        <div className="duc-feature-tile">
          <span className="duc-feature-tile-icon"><ChatIcon /></span>
          <span className="duc-feature-tile-label">AI Chat</span>
        </div>
        <div className="duc-feature-tile">
          <span className="duc-feature-tile-icon"><FlashcardIcon /></span>
          <span className="duc-feature-tile-label">Flashcards</span>
        </div>
        <div className="duc-feature-tile">
          <span className="duc-feature-tile-icon"><QuizIcon /></span>
          <span className="duc-feature-tile-label">Quizzes</span>
        </div>
        <div className="duc-feature-tile">
          <span className="duc-feature-tile-icon"><GuideIcon /></span>
          <span className="duc-feature-tile-label">Study Guides</span>
        </div>
      </div>

      <div className="onboarding-actions duc-actions">
        <button className="onboarding-btn secondary" onClick={goBack}>
          ← Back
        </button>
        <button className="duc-skip" onClick={skipStep}>
          Skip <span className="duc-skip-note">(you can add later)</span>
        </button>
        <button
          className={`onboarding-btn primary${totalDocs > 0 ? ' duc-next--active' : ''}`}
          onClick={goNext}
          disabled={totalDocs === 0}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
}
